import { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import JSZip from "jszip";
import ReportColumns from "../../components/AdhocReport/ReportColumns";
import CustomToolbar from "../../components/AdhocReport/CustomToolbar";
import axios from "axios";
import dayjs from "dayjs";

import NavBar from "../../components/navBar/navBar";
import { ApiManager } from "../../servicemanager/apimanager";
import ReportDropdown from "../../components/AdhocReport/ReportDropdown";
import { LoaderContext } from "../../context/LoaderContext";
import { TYPES_OF_REPORTS } from "../../utils/constants";
import logUserAction from "../../apis/userLogger";
import { buildEcrfPdfFilename, generateEcrfPdfBlob } from "../../utils/ecrfPdfUtils";
import { generateTrialPoemZip } from "../../utils/archivePoemZipUtils";
import { idsParam } from "../../utils/helpers";
import { __API_URL__ } from "../../SERVER_URL";
import {
  useReportStatusQuery,
  useVisitReportRowsQuery,
  useStartReportGenerationMutation,
  useStartDownloadZipMutation,
} from "../../hooks/useStartReportGenerationMutation";

const AdHocReport = () => {
  const user_data = JSON.parse(localStorage.getItem("user_data"));
  const token = localStorage.getItem("token");
  const role = user_data.role;

  const loaderContext = useContext(LoaderContext);

  const [allInvestigators, setAllInvestigators] = useState([]);
  const [AllCroData, setAllCroData] = useState([]);
  const [AllTrialData, setAllTrialData] = useState([]);
  const [AllSubjectId, setAllSubjectId] = useState([]);
  const [AllPhotoData, setAllPhotoData] = useState([]);
  const [AllVideoData, setAllVideoData] = useState([]);
  const [blocksData, setBlocskData] = useState([]);
  const [changeLogBlockNameData, setChangeLogBlockNameData] = useState([]);
  const [visitData, setVisitData] = useState([]);
  const [siteIdData, setsiteIdData] = useState([]);
  const [participantIdData, setParticipantIdData] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [filterConfigurationLastStateSaved, setFilterConfigurationLastStateSaved] = useState(true);
  const [trialChanged, setTrialChanged] = useState(false);
  const [reportName, setReportName] = useState("");
  const [isImagesReportDownloadDisabled, setIsImagesReportDownloadDisabled] = useState(false);
  const [downloadErrorMessage, setDownloadErrorMessage] = useState("");
  const [reportDownloadLink, setReportDownloadLink] = useState(null);
  const [isZipLoading, setIsZipLoading] = useState(false);
  const [reportTableConfigurations, setReportTableConfigurations] = useState({
    noOfRows: 50,
  });
  const [trialDetails, setTrialDetails] = useState({
    investigators: [],
    cro: [],
    trials: [],
    participant_number: [],
    visit_status: [],
    patient_status: [],
    blocks: [],
    block_name: [],
    change_log_block_names: [],
    block_status: [],
    site_number: [],
    visit: [],
    activity_type: [],
    selected_report: "",
  });
  const [data, setData] = useState([]);
  const [pdfProgress, setPdfProgress] = useState({ current: 0, total: 0 });
  const [poemPdfProgress, setPoemPdfProgress] = useState({ current: 0, total: 0 });
  const [job, setJob] = useState(null);
  const [zipJob, setZipJob] = useState(null);
  const [totalReportRows, setTotalReportRows] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: reportTableConfigurations.noOfRows,
  });
  const limit = paginationModel.pageSize;
  const offset = paginationModel.page * paginationModel.pageSize;

  const startMutation = useStartReportGenerationMutation();
  const startZipMutation = useStartDownloadZipMutation();
  const { data: reportJobData } = useReportStatusQuery(job?.statusUrl, {
    enabled: !!job?.statusUrl,
  });

  const { data: zipJobData } = useReportStatusQuery(zipJob?.statusUrl, {
    enabled: !!zipJob?.statusUrl,
  });

  const hasNoDataGridPreview =
    trialDetails.selected_report === TYPES_OF_REPORTS.ECRF_CHANGE_LOGS ||
    trialDetails.selected_report === TYPES_OF_REPORTS.QUERY_CHANGE_LOGS;

  const {
    data: fetchedReportsData,
    isLoading,
    error: rowFetchingError,
  } = useVisitReportRowsQuery({
    rowsUrlBase: job?.rowsUrlBase,
    limit,
    offset,
    enabled: !!job?.rowsUrlBase && reportJobData?.status === "SUCCESS" && !hasNoDataGridPreview,
  });

  useEffect(() => {
    if (!rowFetchingError) return;

    // ignore cancels
    const code = rowFetchingError?.code;
    const name = rowFetchingError?.name;
    if (code === "ERR_CANCELED" || name === "CanceledError") return;

    const status = rowFetchingError?.response?.status;
    if (status === 404) toast.error("This report has expired. Please generate a new one.");
    else if (status === 409) toast.error("Report is not ready yet. Please try again.");
    else toast.error("Failed to load report rows.");
  }, [rowFetchingError]);

  const handleStart = async () => {
    const { jobId, statusUrl, rowsUrlBase } = await startMutation.mutateAsync({
      reportType: trialDetails.selected_report,
      payload: getTrialDetailsPayload(),
    });

    setJob({ jobId, statusUrl, rowsUrlBase });
  };

  useEffect(() => {
    if (!reportJobData) return;

    const {
      total_rows: totalRows,
      file_url: fileUrl,
      manifest_blob_path: blobPath,
    } = reportJobData;

    setTotalReportRows(totalRows);

    if (fileUrl) {
      setReportDownloadLink(fileUrl || null);
    } else if (blobPath && job?.jobId) {
      (async () => {
        try {
          const { jobId, statusUrl } = await startZipMutation.mutateAsync({
            reportType: trialDetails.selected_report,
            jobId: job.jobId,
          });
          setZipJob({ jobId, statusUrl });
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Failed to start zip job");
        }
      })();
    }
  }, [reportJobData]);

  useEffect(() => {
    if (!zipJobData) return;

    const { file_url: fileUrl } = zipJobData;

    setIsZipLoading(zipJobData?.status !== "SUCCESS" && zipJobData?.status !== "FAILED");

    setReportDownloadLink(fileUrl || null);
  }, [zipJobData]);

  useEffect(() => {
    if (!fetchedReportsData) return;

    const { results } = fetchedReportsData;

    setData(fetchedReportsData || {});
    setReportData(results || []);
  }, [fetchedReportsData]);

  const isRunning = reportJobData?.status === "PENDING" || reportJobData?.status === "RUNNING";

  const onSelectedReportChange = (e) => {
    logUserAction(`Selected Type of Report: ${e.target.value}`);
    const { name, value } = e.target;
    setTrialDetails((trialDetails) => ({
      ...trialDetails,
      [name]: value,
    }));
  };

  const getTrialDetailsPayload = () => {
    // Generate a custom payload
    const trialDetailsPayload = { ...trialDetails };

    // Use original site ids list if none are selected
    if (!trialDetailsPayload?.site_number?.length > 0) trialDetailsPayload.site_number = siteIdData;

    // Use original participant ids list if none are selected
    if (!trialDetailsPayload?.participant_number?.length > 0)
      trialDetailsPayload.participant_number = participantIdData;

    logUserAction(`Filter Apply on Report generation: ${trialDetailsPayload?.selected_report}`);
    return trialDetailsPayload;
  };

  //conditions
  if (
    user_data.role === "Investigator" ||
    user_data.role === "Sub_investigator" ||
    user_data.role === "Inv_coordinator"
  ) {
    trialDetails.investigators = [allInvestigators[0]];
  }

  if (
    user_data.role === "CRO Admin" ||
    user_data.role === "Cro_coordinator" ||
    user_data.role === "Data Manager"
  ) {
    trialDetails.cro = [AllCroData[0]];
  }

  const AllInvestigatorsData = () => {
    loaderContext.setLoaderCount((prev) => prev + 1);

    axios
      .get(`${__API_URL__}/api/photos/report_investigatorslist`, {
        params: { trial_ids: idsParam(trialDetails?.trials) },
        headers: {
          Authorization: `Token ${token}`,
        },
      })
      .then((res) => {
        setAllInvestigators(res.data);
      })
      .finally(() => {
        loaderContext.setLoaderCount((prev) => prev - 1);
      });
  };

  const REPORT_TYPE = [
    { value: "key_value", label: "Ecrf Report Key Value" },
    { value: "key_column", label: "Ecrf Report Key as Column" },
  ];

  if (trialDetails?.selected_report === TYPES_OF_REPORTS.ECRF) {
    REPORT_TYPE.push({ value: "filled_block", label: "Ecrf Filled Block Report" });
    REPORT_TYPE.push({ value: "ecrf_filled_pdf", label: "Export eCRF Filled PDF" });
  }
  const AllCroList = () => {
    loaderContext.setLoaderCount((prev) => prev + 1);

    axios
      .get(`${__API_URL__}/api/photos/report_crolist`, {
        params: { trial_ids: idsParam(trialDetails?.trials) },
        headers: {
          Authorization: `Token ${token}`,
        },
      })
      .then((res) => {
        setAllCroData(res.data);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        loaderContext.setLoaderCount((prev) => prev - 1);
      });
  };

  const getExternalPhotoTypes = () => {
    loaderContext.setLoaderCount((prev) => prev + 1);

    axios
      .get(`${__API_URL__}/api/photos/trials_external_photo_types/`, {
        params: { trial_ids: idsParam(trialDetails?.trials) },
        headers: {
          Authorization: `Token ${token}`,
        },
      })
      .then((res) => {
        setAllPhotoData(res.data);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        loaderContext.setLoaderCount((prev) => prev - 1);
      });
  };

  const getExternalVideoTypes = () => {
    loaderContext.setLoaderCount((prev) => prev + 1);

    axios
      .get(`${__API_URL__}/api/photos/trials_external_video_types/`, {
        params: { trial_ids: idsParam(trialDetails?.trials) },
        headers: {
          Authorization: `Token ${token}`,
        },
      })
      .then((res) => {
        setAllVideoData(res.data);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        loaderContext.setLoaderCount((prev) => prev - 1);
      });
  };

  const AllPhotoType = () => {
    loaderContext.setLoaderCount((prev) => prev + 1);

    axios
      .get(`${__API_URL__}/api/photos/report-photo-types/web/`, {
        params: { trial_ids: idsParam(trialDetails?.trials) },
        headers: {
          Authorization: `Token ${token}`,
        },
      })
      .then((res) => {
        setAllPhotoData(res.data);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        loaderContext.setLoaderCount((prev) => prev - 1);
      });
  };

  const AllSubjectEmailList = () => {
    loaderContext.setLoaderCount((prev) => prev + 1);

    axios
      .get(`${__API_URL__}/api/photos/report_patientlist/`, {
        params: { trial_ids: idsParam(trialDetails?.trials) },
        headers: {
          Authorization: `Token ${token}`,
        },
      })
      .then((res) => {})
      .catch((err) => {
        console?.error(err);
        return [];
      })
      .finally(() => {
        loaderContext.setLoaderCount((prev) => prev - 1);
      });
  };

  const AllSubjectIdList = () => {
    loaderContext.setLoaderCount((prev) => prev + 1);

    axios
      .get(`${__API_URL__}/api/photos/report_subjectid_list/`, {
        params: { trial_ids: idsParam(trialDetails?.trials) },
        headers: {
          Authorization: `Token ${token}`,
        },
      })
      .then((res) => {
        setAllSubjectId(res.data);
      })
      .catch((err) => {
        console?.error(err);
        return [];
      })
      .finally(() => {
        loaderContext.setLoaderCount((prev) => prev - 1);
      });
  };

  const GetReportFileName = () => {
    const trialId =
      trialDetails?.trials && trialDetails.trials.length > 0 ? trialDetails.trials[0]?.id : null;
    const siteId =
      trialDetails?.site_number && trialDetails.site_number.length > 0
        ? trialDetails.site_number[0]?.value
        : 999;

    let blockName = null;
    if (trialDetails?.blocks && trialDetails.blocks.length === 1) {
      blockName = trialDetails.blocks[0]?.label;
    }

    const urlParams = {
      trial: trialId,
      site_id: siteId,
      report_type: trialDetails?.selected_report,
      blocks: blockName ? blockName : "", // Append block name only if it exists
    };

    loaderContext.setLoaderCount((prev) => prev + 1);

    axios
      .get(`${__API_URL__}/api/photos/create_report_filename/`, {
        params: urlParams,
        headers: {
          Authorization: `Token ${token}`,
        },
      })
      .then((res) => {
        setReportName(res.data?.file_name);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        loaderContext.setLoaderCount((prev) => prev - 1);
      });
  };

  const getTrialsList = async () => {
    loaderContext.setLoaderCount((prev) => prev + 1);

    try {
      const response = await ApiManager.get(`${__API_URL__}/api/photos/report_trials`, token);
      setAllTrialData(response.data);
    } catch (error) {
      console?.error(error);
      return [];
    } finally {
      loaderContext.setLoaderCount((prev) => prev - 1);
    }
  };

  const Blocks = () => {
    loaderContext.setLoaderCount((prev) => prev + 1);

    axios
      .get(`${__API_URL__}/api/ecrf/get_allblocks/`, {
        params: {
          trials: idsParam(trialDetails?.trials),
          visits: idsParam(trialDetails?.visit, "value"),
        },
        headers: {
          Authorization: `Token ${token}`,
        },
      })
      .then((res) => {
        const oldBlocks = res.data.old_blocks.map((block) => ({
          ...block,
          isNew: false,
        }));
        const newBlocks = res.data.new_blocks.map((block) => ({
          ...block,
          isNew: true,
        }));
        setBlocskData([...oldBlocks, ...newBlocks]);
        setTrialChanged(true);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        loaderContext.setLoaderCount((prev) => prev - 1);
      });
  };

  const GetAllVisits = () => {
    loaderContext.setLoaderCount((prev) => prev + 1);

    axios
      .get(`${__API_URL__}/api/ecrf/get_allvisits/`, {
        params: { trials: idsParam(trialDetails?.trials) },
        headers: {
          Authorization: `Token ${token}`,
        },
      })
      .then((res) => {
        setVisitData(res.data);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        loaderContext.setLoaderCount((prev) => prev - 1);
      });
  };

  const AllSiteIds = () => {
    loaderContext.setLoaderCount((prev) => prev + 1);

    axios
      .get(`${__API_URL__}/api/photos/site_id`, {
        params: { trial_ids: idsParam(trialDetails?.trials) },
        headers: {
          Authorization: `Token ${token}`,
        },
      })
      .then((res) => {
        setsiteIdData(res.data);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        loaderContext.setLoaderCount((prev) => prev - 1);
      });
  };

  const AllChangeLogBlockNames = () => {
    loaderContext.setLoaderCount((prev) => prev + 1);

    axios
      .get(`${__API_URL__}/api/ecrf/change_log_block_names/`, {
        params: { trials: idsParam(trialDetails?.trials) },
        headers: {
          Authorization: `Token ${token}`,
        },
      })
      .then((res) => {
        setChangeLogBlockNameData(res.data);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        loaderContext.setLoaderCount((prev) => prev - 1);
      });
  };

  const AllParticipantIds = () => {
    const siteNumbers =
      trialDetails?.site_number?.length > 0 ? trialDetails?.site_number : siteIdData;

    loaderContext.setLoaderCount((prev) => prev + 1);

    axios
      .get(`${__API_URL__}/api/photos/participant_id/`, {
        params: {
          trial_ids: idsParam(trialDetails?.trials),
          site_ids: idsParam(siteNumbers, "value"),
        },
        headers: {
          Authorization: `Token ${token}`,
        },
      })
      .then((res) => {
        setParticipantIdData(res.data);
      })
      .catch((err) => {
        console?.error(err);
        return [];
      })
      .finally(() => {
        loaderContext.setLoaderCount((prev) => prev - 1);
      });
  };

  const handleEcrfFilledPdfDownload = async () => {
    const payload = getTrialDetailsPayload();
    loaderContext.setLoaderCount((prev) => prev + 1);
    setPdfProgress({ current: 0, total: 0 });

    let manifest;
    try {
      const res = await axios.post(`${__API_URL__}/api/ecrf/ecrf_filled_pdf_manifest/`, payload, {
        headers: { Authorization: `Token ${token}` },
      });
      manifest = res.data.results;
    } catch {
      toast.error("Failed to fetch PDF manifest.");
      loaderContext.setLoaderCount((prev) => prev - 1);
      return;
    }

    if (!manifest?.length) {
      toast.info("No visits found for the selected filters.");
      loaderContext.setLoaderCount((prev) => prev - 1);
      return;
    }

    const zip = new JSZip();
    const timestamp = dayjs().format("YYYYMMDDTHHmmss");
    setPdfProgress({ current: 0, total: manifest.length });

    const failed = [];

    for (let i = 0; i < manifest.length; i++) {
      const entry = manifest[i];
      try {
        const res = await axios.get(
          `${__API_URL__}/api/ecrf/get_ecrf_blocks_per_visit/${entry.visit_id}/?patient_id=${entry.patient_id}&session_id=${entry.session_id}`,
          { headers: { Authorization: `Token ${token}` } }
        );
        const blob = await generateEcrfPdfBlob(res.data.results, {
          trialName: entry.trial_name,
          visitName: entry.visit_name,
          siteId: entry.site_id,
          participantId: entry.participant_id,
        });
        const filename = buildEcrfPdfFilename(
          entry.trial_name,
          entry.site_id,
          entry.participant_id,
          entry.visit_name,
          timestamp
        );
        zip.folder(`${entry.site_id}/${entry.participant_id}`).file(`${filename}.pdf`, blob);
      } catch {
        failed.push(`${entry.participant_id} — ${entry.visit_name}`);
      }
      setPdfProgress({ current: i + 1, total: manifest.length });
    }

    const zipBlob = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ecrf_filled_pdfs_${timestamp}.zip`;
    a.click();
    URL.revokeObjectURL(url);

    if (failed.length > 0) {
      toast.warn(`${failed.length} visit(s) could not be exported:\n${failed.join("\n")}`, {
        autoClose: false,
      });
    }

    setPdfProgress({ current: 0, total: 0 });
    loaderContext.setLoaderCount((prev) => prev - 1);
  };

  const handlePoemPdfDownload = async () => {
    const payload = getTrialDetailsPayload();
    loaderContext.setLoaderCount((prev) => prev + 1);
    setPoemPdfProgress({ current: 0, total: 0 });

    let result;
    try {
      result = await generateTrialPoemZip({
        manifestPayload: payload,
        token,
        individualVisits: !!trialDetails.poem_individual_visits,
        onProgress: setPoemPdfProgress,
      });
    } catch {
      toast.error("Failed to generate POEMs PDFs.");
      loaderContext.setLoaderCount((prev) => prev - 1);
      setPoemPdfProgress({ current: 0, total: 0 });
      return;
    }

    const { zipBlob, failedEntries } = result;

    if (!zipBlob) {
      toast.info("No completed POEMs sessions found for the selected filters.");
      loaderContext.setLoaderCount((prev) => prev - 1);
      setPoemPdfProgress({ current: 0, total: 0 });
      return;
    }

    const timestamp = dayjs().format("YYYYMMDDTHHmmss");
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `poem_filled_pdfs_${timestamp}.zip`;
    a.click();
    URL.revokeObjectURL(url);

    if (failedEntries.length > 0) {
      toast.warn(
        `${failedEntries.length} entr${failedEntries.length === 1 ? "y" : "ies"} could not be exported:\n${failedEntries.join("\n")}`,
        { autoClose: false }
      );
    }

    setPoemPdfProgress({ current: 0, total: 0 });
    loaderContext.setLoaderCount((prev) => prev - 1);
  };

  const getDownloadDataView = (e) => {
    if (!trialDetails?.selected_report) {
      toast.error("Select a Type of Report.");
      return;
    }

    if (!(trialDetails?.trials?.length > 0)) {
      toast.error("Please select a Trial.");
      return;
    }

    const REPORT_VALUES = Object.values(TYPES_OF_REPORTS);

    if (REPORT_VALUES.includes(trialDetails?.selected_report) && !trialDetails?.trials?.length) {
      toast.error("Please select a Trial.");
      return;
    }

    if (
      trialDetails.selected_report === TYPES_OF_REPORTS.ECRF_DISCREPANCY &&
      !trialDetails?.discrepancy_type?.length
    ) {
      toast.error("Please select all required fields.");
      return;
    }

    if (
      trialDetails.selected_report === TYPES_OF_REPORTS.ECRF &&
      trialDetails.reportType?.value === "ecrf_filled_pdf"
    ) {
      handleEcrfFilledPdfDownload();
      return;
    }

    if (
      trialDetails.selected_report === TYPES_OF_REPORTS.ECRF ||
      trialDetails.selected_report === TYPES_OF_REPORTS.VISIT ||
      trialDetails.selected_report === TYPES_OF_REPORTS.PHOTO ||
      trialDetails.selected_report === TYPES_OF_REPORTS.EXTERNAL_PHOTOS ||
      trialDetails.selected_report === TYPES_OF_REPORTS.EXTERNAL_VIDEOS ||
      trialDetails.selected_report === TYPES_OF_REPORTS.POEM ||
      trialDetails.selected_report === TYPES_OF_REPORTS.ADVANCED_SURVEY ||
      trialDetails.selected_report === TYPES_OF_REPORTS.EDIARY ||
      trialDetails.selected_report === TYPES_OF_REPORTS.AI_SCORE ||
      trialDetails.selected_report === TYPES_OF_REPORTS.APP_PHOTO_SCORE ||
      trialDetails.selected_report === TYPES_OF_REPORTS.EXTERNAL_PHOTO_SCORE ||
      trialDetails.selected_report === TYPES_OF_REPORTS.VIDEO ||
      trialDetails.selected_report === TYPES_OF_REPORTS.EXTERNAL_DOCUMENTS ||
      trialDetails.selected_report === TYPES_OF_REPORTS.ECONSENT ||
      trialDetails.selected_report === TYPES_OF_REPORTS.ECRF_CHANGE_LOGS ||
      trialDetails.selected_report === TYPES_OF_REPORTS.QUERY_CHANGE_LOGS ||
      trialDetails.selected_report === TYPES_OF_REPORTS.ECRF_DISCREPANCY
    ) {
      handleStart();
      return;
    }
  };

  useEffect(() => {
    AllSubjectEmailList();
    if (role === "Super Admin") {
      AllSubjectIdList();
    }
    getTrialsList();
    AllInvestigatorsData();
    AllCroList();
    GetAllVisits();
    AllSiteIds();
    if (trialDetails.selected_report === TYPES_OF_REPORTS.PHOTO) {
      AllPhotoType();
    }
  }, [trialDetails?.trials]);

  useEffect(() => {
    AllParticipantIds();
  }, [siteIdData, trialDetails?.site_number]);

  useEffect(() => {
    if (trialDetails.selected_report === TYPES_OF_REPORTS.PHOTO) {
      AllPhotoType();
    } else if (trialDetails.selected_report === TYPES_OF_REPORTS.EXTERNAL_PHOTOS) {
      getExternalPhotoTypes();
    } else if (trialDetails.selected_report === TYPES_OF_REPORTS.EXTERNAL_VIDEOS) {
      getExternalVideoTypes();
    }
  }, [trialDetails.selected_report]);

  useEffect(() => {
    if (trialDetails?.trials && trialDetails.trials.length > 0) {
      GetReportFileName();
    }
  }, [
    trialDetails.selected_report,
    trialDetails.trials,
    trialDetails.site_number,
    trialDetails.blocks,
  ]);

  useEffect(() => {
    if (trialDetails?.selected_report === TYPES_OF_REPORTS.ECRF) {
      Blocks();
    }
  }, [trialDetails.selected_report, trialDetails?.trials, trialDetails?.visit]);

  useEffect(() => {
    if (trialDetails?.selected_report === TYPES_OF_REPORTS.ECRF_CHANGE_LOGS) {
      AllChangeLogBlockNames();
    }
  }, [trialDetails.selected_report, trialDetails?.trials]);

  useEffect(() => {
    if (trialDetails?.selected_report === TYPES_OF_REPORTS.VIDEO) {
      AllSiteIds();
    }
  }, [trialDetails.selected_report]);

  useEffect(() => {
    if (
      trialDetails.selected_report === TYPES_OF_REPORTS.ECRF ||
      trialDetails.selected_report === TYPES_OF_REPORTS.PHOTO
    ) {
      setTrialDetails((prevTrialDetails) => ({
        ...prevTrialDetails,
        trials: [],
        visit: [],
      }));
    }
  }, [trialDetails.selected_report]);

  useEffect(() => {
    if (trialChanged) {
      setTrialDetails((prev) => ({
        ...prev,
        blocks: blocksData,
      }));
    }
  }, [trialChanged, blocksData]);

  useEffect(() => {
    let isImageHidden = trialDetails?.trials[0]?.is_hide_image || false;

    // Check for isImageHidden only for CRO and related roles
    if (role !== "CRO Admin" && role !== "Cro_coordinator" && role !== "Data Manager")
      isImageHidden = false;

    if (trialDetails?.selected_report === TYPES_OF_REPORTS.PHOTO) {
      // If is_hide_image is true for CRO and related roles
      if (isImageHidden) {
        setIsImagesReportDownloadDisabled(true);
        setDownloadErrorMessage(
          `${role} cannot view/download images as the "Hide Subject Photos" option is enabled for this trial.`
        );
        return;
      }
    }

    // Everything is okay, enable download
    setIsImagesReportDownloadDisabled(false);
    setDownloadErrorMessage("");
  }, [reportData, trialDetails, role]);

  useEffect(() => {
    setReportData([]);
    setReportDownloadLink(null);
    setJob(null);
    setTotalReportRows(0);
    setData([]);
  }, [trialDetails]);

  const downloadReport = () => {
    if (reportDownloadLink) {
      window.open(reportDownloadLink, "_blank");
    } else {
      toast.error("No data to download.");
    }
  };

  return (
    <Box sx={{ maxWidth: "95%", mx: "auto" }}>
      <NavBar active="adhoc-report" />
      <Box mt={2}>
        <FormControl sx={{ minWidth: 220, mb: 2 }} size="small">
          <InputLabel id="selected-report-label">Type of Report *</InputLabel>
          <Select
            labelId="selected-report-label"
            value={trialDetails?.selected_report}
            name="selected_report"
            label="Type of Report *"
            onChange={onSelectedReportChange}
            required
            SelectDisplayProps={{ "data-testid": "report-type-select-trigger" }}
          >
            <MenuItem value="">
              <em>Select</em>
            </MenuItem>
            {/* 22/09/2025: Removed the implementation of general report */}
            <MenuItem value={TYPES_OF_REPORTS.VISIT}>Visit Report</MenuItem>
            <MenuItem value={TYPES_OF_REPORTS.ECRF}>Ecrf Report</MenuItem>
            <MenuItem value={TYPES_OF_REPORTS.PHOTO}>APP Photos Report</MenuItem>
            <MenuItem value={TYPES_OF_REPORTS.VIDEO}>APP Videos Report</MenuItem>
            <MenuItem value={TYPES_OF_REPORTS.EXTERNAL_PHOTOS}>External Photos Report</MenuItem>
            <MenuItem value={TYPES_OF_REPORTS.EXTERNAL_VIDEOS}>External Videos Report</MenuItem>
            <MenuItem value={TYPES_OF_REPORTS.EXTERNAL_DOCUMENTS}>
              External Documents Report
            </MenuItem>
            <MenuItem value={TYPES_OF_REPORTS.POEM}>POEMs Report</MenuItem>
            <MenuItem value={TYPES_OF_REPORTS.ADVANCED_SURVEY}>ePRO Report</MenuItem>
            <MenuItem value={TYPES_OF_REPORTS.EDIARY}>eDiary Report</MenuItem>
            <MenuItem value={TYPES_OF_REPORTS.AI_SCORE}>AI Score Report</MenuItem>
            <MenuItem value={TYPES_OF_REPORTS.APP_PHOTO_SCORE}>
              App Photos Manual Score Report
            </MenuItem>
            <MenuItem value={TYPES_OF_REPORTS.EXTERNAL_PHOTO_SCORE}>
              External Photos Manual Score Report
            </MenuItem>
            <MenuItem value={TYPES_OF_REPORTS.ECONSENT}>eConsent Report</MenuItem>
            <MenuItem value={TYPES_OF_REPORTS.ECRF_CHANGE_LOGS}>Ecrf Change Logs Report</MenuItem>
            <MenuItem value={TYPES_OF_REPORTS.QUERY_CHANGE_LOGS}>Query Change Logs Report</MenuItem>
            <MenuItem value={TYPES_OF_REPORTS.ECRF_DISCREPANCY}>eCRF Discrepancy Report</MenuItem>
          </Select>
        </FormControl>

        <ReportDropdown
          trialDetails={trialDetails}
          setTrialDetails={setTrialDetails}
          blocksData={blocksData}
          changeLogBlockNameData={changeLogBlockNameData}
          visitData={visitData}
          AllTrialData={AllTrialData}
          AllPhotoData={AllPhotoData}
          AllVideoData={AllVideoData}
          allInvestigators={allInvestigators}
          AllCroData={AllCroData}
          AllSubjectId={AllSubjectId}
          siteIdData={siteIdData}
          participantIdData={participantIdData}
          user_data={user_data}
          download_link={data?.download_link}
          getDownloadDataView={getDownloadDataView}
          REPORT_TYPE={REPORT_TYPE}
          reportDownloadLink={reportDownloadLink}
          downloadReport={downloadReport}
          isLoading={isLoading}
          isRunning={isRunning}
          pdfProgress={pdfProgress}
          handlePoemPdfDownload={handlePoemPdfDownload}
          poemPdfProgress={poemPdfProgress}
        />

        <ReportColumns
          CustomToolbar={CustomToolbar}
          reportName={reportName}
          totalCount={totalReportRows}
          paginationModel={paginationModel}
          setPaginationModel={setPaginationModel}
          setFilterConfigurationLastStateSaved={setFilterConfigurationLastStateSaved}
          reportData={reportData}
          setReportData={setReportData}
          data={data}
          setData={setData}
          trialDetails={trialDetails}
          reportTableConfigurations={reportTableConfigurations}
          role={role}
          toast={toast}
          isImagesReportDownloadDisabled={isImagesReportDownloadDisabled}
          downloadErrorMessage={downloadErrorMessage}
          reportDownloadLink={reportDownloadLink}
          downloadReport={downloadReport}
          isZipLoading={isZipLoading}
        />
      </Box>
    </Box>
  );
};
export default AdHocReport;
